import React, { forwardRef, useMemo, useRef } from 'react';
import { activeHidden } from '../common/BlockVisibility';
import { EmptyState } from '../../components/EmptyState/EmptyState';
import { defaultRenderImage } from '../../utils/image/renderImage';
import { sanitizeHtml } from '../../utils/sanitize';
import { mapEntries, mapUnits } from './mapEntries';
import { useFormUnits } from '../../hooks/useFormUnits';
import type { GroupableUnit } from '@autodromo/units';
import { SimuladorSection } from './SimuladorFinanciamento.styles';
import { SimuladorFinanciamentoSkeleton } from './SimuladorFinanciamentoSkeleton';
import {
  DEFAULT_INSTALLMENT_TERMS,
  DEFAULT_SUMMARY_LEGAL_TEXT,
  DISCLAIMER_TEXT,
  type SimuladorFinanciamentoProps,
} from './types';
import { WizardProvider, initialWizardState, useWizard } from './wizardContext';
import { onlyDigits } from './utils/finance';
import { getOrCreateVisitorId } from '../../utils/visitorId';
import { CampaignTracking } from '../../utils/CampaignTracking';
import { useAntiBot, ANTIBOT_ACTIONS } from '../../hooks/useAntiBot';
import type { AntiBotMeta } from '../../constants/antiBot';
import {
  LEAD_CHANNEL,
  captureLeadAttemptFor,
  submitConversion,
  type ConversionPayload,
  type LeadStage,
} from './utils/lead';
import { Stepper } from './parts/Stepper';
import { MobileVehicle, VehiclePanel } from './parts/VehiclePanel';
import { Step1Modelo } from './steps/Step1Modelo';
import { Step2Dados } from './steps/Step2Dados';
import { Step3Seminovo } from './steps/Step3Seminovo';
import { Step4Simulacao } from './steps/Step4Simulacao';
import { Step5Resumo } from './steps/Step5Resumo';

export type { SimuladorFinanciamentoProps };

/* ---------- layout interno + envio (conversão em 2 etapas) ---------- */
const WizardLayout = ({
  accountId,
  siteId,
  teamId,
  antiBot,
}: {
  accountId?: string;
  siteId?: string;
  teamId?: string;
  antiBot?: AntiBotMeta | null;
}) => {
  const wizard = useWizard();
  const { state, set, model, version, unit, result, vehicles, units, config } = wizard;
  // Token antibot: sem ele o crm-api bloqueia a conversão (403) onde o antibot
  // está ativo — mesmo caminho dos blocos de formulário.
  const { execute: getAntiBotToken, requestScript: requestAntiBotScript } = useAntiBot(antiBot);
  // Evita reenviar a conversão de dados pessoais se o visitante voltar e avançar
  // de novo na etapa 2 (não duplica o lead da 1ª etapa na mesma sessão).
  const personalSentRef = useRef(false);
  const proposalSentRef = useRef(false);

  /** Registra a conversão da etapa. `channel` diferencia dados pessoais x
      simulação no CRM (evita leitura de "lead duplicado"). */
  const submitLead = async (stage: LeadStage) => {
    if (stage === 'personal') {
      if (personalSentRef.current) return;
      personalSentRef.current = true;
    }
    if (stage === 'proposal') {
      if (proposalSentRef.current) return;
      proposalSentRef.current = true;
    }
    const p = state.personal;
    const product = [model?.name, version?.name].filter(Boolean).join(' ') || 'Simulação de financiamento';
    const realUnitId = unit && !unit.id.startsWith('fallback') ? unit.id : undefined;

    const extra: Record<string, unknown> =
      stage === 'proposal'
        ? {
            valor_veiculo: result.vehiclePrice,
            entrada: result.downPayment,
            seminovo: result.tradeInValue,
            valor_financiado: result.financed,
            prazo: `${result.term}x`,
            parcela: result.installment,
          }
        : { contato_preferido: p.contactPrefs.join(', ') };
    if (p.cpf) extra.cpf = p.cpf;

    const campaignTracking = CampaignTracking.getTrackingData();

    const payload: ConversionPayload = {
      accountId,
      siteId,
      name: p.name,
      email: p.email,
      phone: onlyDigits(p.phone),
      product,
      channel: LEAD_CHANNEL[stage],
      unitId: realUnitId,
      ...(teamId && { teamId }),
      visitorId: getOrCreateVisitorId() ?? undefined,
      antiBotAction: ANTIBOT_ACTIONS.FORM_SUBMIT,
      ...(typeof window !== 'undefined' && { origin_url: window.location.href }),
      ...(Object.keys(campaignTracking).length > 0 && { campaign: campaignTracking }),
      ...extra,
    };

    const captureId = captureLeadAttemptFor(payload, stage);

    // O token nunca pode travar o envio: se o recaptcha demorar/falhar, segue
    // sem token (crm-api aceita onde o antibot é fail-open; onde é obrigatório,
    // ao menos a requisição dispara em vez de sumir silenciosamente).
    try {
      payload.antiBotToken =
        (await Promise.race([
          getAntiBotToken(ANTIBOT_ACTIONS.FORM_SUBMIT),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000)),
        ])) ?? undefined;
    } catch {
      payload.antiBotToken = undefined;
    }

    // Evento no document: ponto de integração/tracking no site publicado.
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('lpfs:conversion', { detail: { stage, payload } }));
    }
    void submitConversion(payload, captureId);
  };

  const onPersonalComplete = () => void submitLead('personal');

  const onSubmit = () => {
    set((d) => {
      d.submitted = true;
    });
    void submitLead('proposal');
  };

  const onRestart = () => {
    personalSentRef.current = false;
    proposalSentRef.current = false;
    set((d) => {
      const first = vehicles[0];
      Object.assign(d, initialWizardState(first, units[0]?.id, config.terms));
    });
  };

  return (
    <div className="lpfs-container" onFocus={requestAntiBotScript}>
      <Stepper />
      <div className="lpfs-grid">
        <div className="lpfs-content">
          {state.step === 1 && <Step1Modelo />}
          {state.step === 2 && <Step2Dados onComplete={onPersonalComplete} />}
          {state.step === 3 && <Step3Seminovo />}
          {state.step === 4 && <Step4Simulacao />}
          {state.step === 5 && <Step5Resumo onSubmit={onSubmit} onRestart={onRestart} />}
        </div>
        <VehiclePanel />
      </div>
      {/* No fim do container: sticky bottom fica contido na área do bloco. */}
      <MobileVehicle />
    </div>
  );
};

export const SimuladorFinanciamento = forwardRef<HTMLDivElement, SimuladorFinanciamentoProps>((props, ref) => {
  const {
    htmlId,
    title,
    titleVisible = true,
    titleTag = 'h2',
    description,
    descriptionVisible = true,
    privacyPolicyText = '',
    privacyPolicyUrl = '',
    privacyPolicyRedirect,
    showCpf = true,
    showBirthdate = true,
    installmentTerms = DEFAULT_INSTALLMENT_TERMS,
    summaryLegalText = DEFAULT_SUMMARY_LEGAL_TEXT,
    submitEnabled = true,
    ctaButton,
    activePalette,
    backgroundType = 'color',
    bgColor,
    backgroundImage,
    titleColor,
    bodyColor,
    accentColor,
    ctaBgColor,
    ctaTextColor,
    padding,
    hideOnMobile,
    hideOnDesktop,
    dynamicStructure,
    vehicles: vehiclesProp,
    units: unitsProp,
    loading = false,
    renderImage = defaultRenderImage,
    accountId,
    siteId,
    teamId,
    antiBot,
    showEmptyWarning = false,
    useGlobalQueryParams,
  } = props;

  const resolved = useMemo(() => mapEntries(dynamicStructure), [dynamicStructure]);
  const vehicles = useMemo(
    () => (vehiclesProp && vehiclesProp.length ? vehiclesProp : resolved.vehicles),
    [vehiclesProp, resolved.vehicles],
  );
  /* As unidades do registro, senão as da região escolhida, senão as do canal — mesma
     regra dos formulários. Filtra antes do mapUnits, que descarta o `state`. */
  const { units: allowedUnits } = useFormUnits({
    channelUnits: dynamicStructure?.channel?.units as GroupableUnit[] | undefined,
    unitGroupingMode: dynamicStructure?.channel?.unitGroupingMode,
    unitGroups: dynamicStructure?.channel?.unitGroups,
    entry: dynamicStructure?.item,
    useGlobalQueryParams,
  });

  // Sem unidade no canal → a etapa mostra empty state e a unidade fica opcional.
  const units = useMemo(
    () => (unitsProp && unitsProp.length ? unitsProp : mapUnits(allowedUnits)),
    [unitsProp, allowedUnits],
  );
  const hasVehicles = vehicles.length > 0;

  const dataKey = useMemo(
    () => `${vehicles.map((v) => v.id).join('|')}__${units.map((u) => u.id).join('|')}`,
    [vehicles, units],
  );

  const initialState = useMemo(
    () => initialWizardState(vehicles[0], units[0]?.id, installmentTerms),
    [dataKey, vehicles, units, installmentTerms],
  );

  const styleVars = useMemo(() => {
    const s: React.CSSProperties = {};
    if (bgColor) (s as Record<string, string>)['--general-background'] = bgColor;
    if (accentColor) {
      (s as Record<string, string>)['--lpfs-accent'] = accentColor;
      (s as Record<string, string>)['--general-primary'] = accentColor;
    }
    if (titleColor) (s as Record<string, string>)['--lpfs-text-title'] = titleColor;
    if (bodyColor) (s as Record<string, string>)['--lpfs-text-body'] = bodyColor;
    if (ctaBgColor) (s as Record<string, string>)['--lpfs-cta-bg'] = ctaBgColor;
    if (ctaTextColor) (s as Record<string, string>)['--lpfs-cta-text'] = ctaTextColor;
    return s;
  }, [bgColor, accentColor, titleColor, bodyColor, ctaBgColor, ctaTextColor]);

  const bgImage = backgroundType === 'image' ? backgroundImage?.desktop : undefined;
  const TitleTag = (titleTag || 'h2') as keyof JSX.IntrinsicElements;

  // Sem modelos válidos: no builder (showEmptyWarning) mostra um aviso de que o
  // bloco é só pra modelos de veículo — padrão dos blocos dinâmicos, que
  // exibem EmptyState em vez de sumir. No site final fica null (o bloco some, sem
  // mostrar aviso pro visitante).
  if (!loading && !hasVehicles) {
    if (!showEmptyWarning) return null;
    return (
      <SimuladorSection ref={ref} id={htmlId} className="simulador-financiamento" padding={padding}>
        <div className="lpfs-fullbleed">
          <div className="lpfs-shell">
            <EmptyState
              title="Coleção incompatível"
              description="O Simulador de Financiamento funciona apenas com coleções de modelos de veículo. Selecione uma coleção de modelos para exibir o bloco."
            />
          </div>
        </div>
      </SimuladorSection>
    );
  }

  return (
    <SimuladorSection
      ref={ref}
      id={htmlId}
      className={`simulador-financiamento ${activePalette || ''} ${activeHidden({ hideOnMobile, hideOnDesktop })}`.trim()}
      data-testid="simulador-financiamento"
      padding={padding}
      style={
        bgImage
          ? { ...styleVars, backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : styleVars
      }
    >
      <div className="lpfs-fullbleed">
        <div className="lpfs-shell" data-anim-stagger>
          <header className="lpfs-header">
            {titleVisible && !!title && <TitleTag className="lpfs-title" data-af-field="title">{title}</TitleTag>}
            {descriptionVisible && !!description && (
              <div className="lpfs-block-desc" data-af-field="description" dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }} />
            )}
          </header>

          {loading ? (
            <SimuladorFinanciamentoSkeleton />
          ) : (
            <WizardProvider
              key={dataKey}
              vehicles={vehicles}
              units={units}
              renderImage={renderImage}
              config={{
                privacyPolicyText,
                privacyPolicyUrl,
                privacyRedirect: privacyPolicyRedirect,
                showCpf,
                showBirthdate,
                terms: installmentTerms,
                summaryLegalText,
                submitEnabled,
                ctaButton,
              }}
              initialState={initialState}
            >
              <WizardLayout accountId={accountId} siteId={siteId} teamId={teamId} antiBot={antiBot} />
            </WizardProvider>
          )}

          <footer
            className="lpfs-disclaimer"
            style={{ marginTop: 28, fontSize: 12, lineHeight: 1.6, color: 'var(--lpfs-muted)' }}
          >
            {DISCLAIMER_TEXT}
          </footer>
        </div>
      </div>
    </SimuladorSection>
  );
});
